import { UserMark } from "@/app/routes/dashboard/components/UserMark";
import { cn } from "@/utils/helpers";

export type StackPerson = {
  userId: string;
  name: string | null;
  /** Present right now: "active" (tab visible) or "away" (tab hidden). Absent = not here. */
  state?: "active" | "away";
};

const displayName = (person: StackPerson) => person.name?.trim() || "A teammate";

/** "Rana", "Rana and Omar", "Rana, Omar and 2 others". */
export function namesList(people: StackPerson[], max = 2): string {
  const names = people.map(displayName);
  if (names.length <= max) {
    return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }
  const rest = names.length - max;
  return `${names.slice(0, max).join(", ")} and ${rest} ${rest === 1 ? "other" : "others"}`;
}

/**
 * Overlapping person marks. A present person gets a live ring. Someone whose
 * tab is hidden gets a dark tint on the mark, not a fade, so stacked marks
 * stay solid where they overlap.
 */
export function PeopleStack({
  people,
  size = 20,
  max = 4,
  className,
  label,
}: {
  people: StackPerson[];
  size?: number;
  max?: number;
  className?: string;
  /** Accessible summary; defaults to the names. */
  label?: string;
}) {
  if (people.length === 0) return null;
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  const summary = label ?? namesList(people, 3);

  return (
    <span
      className={cn("inline-flex shrink-0 items-center", className)}
      role="img"
      aria-label={summary}
      title={summary}
    >
      {shown.map((person, i) => (
        <span
          key={person.userId}
          className="relative inline-flex rounded-full"
          style={{
            marginInlineStart: i === 0 ? 0 : -Math.round(size / 3),
            zIndex: shown.length - i,
            boxShadow: person.state
              ? `0 0 0 2px var(--co-surface-solid), 0 0 0 3.5px var(--co-live)`
              : "0 0 0 2px var(--co-surface-solid)",
          }}
        >
          <UserMark name={displayName(person)} size={size} />
          {person.state === "away" ? (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{ background: "color-mix(in srgb, #121212 58%, transparent)" }}
            />
          ) : null}
        </span>
      ))}
      {extra > 0 ? (
        <span
          className="ms-1 font-mono text-co-faint"
          style={{ fontSize: Math.max(9, Math.round(size * 0.45)) }}
        >
          +{extra}
        </span>
      ) : null}
    </span>
  );
}

export default PeopleStack;
